import { getPath } from 'routes/router-paths';
import { CreateNewParamId } from 'utils/constants';
import { lazyImport } from './helpers';
import { TRoute } from './types';

export const publicRoutes: TRoute[] = [
  {
    path: getPath('loginPage'),
    element: lazyImport(() => import('modules/Authentication/LoginPage')),
  },
  {
    path: getPath('keycloakAuthenticationPage'),
    element: lazyImport(
      () => import('modules/Authentication/KeycloakAuthenticationPage')
    ),
  },
];

export const privateRoutes: TRoute[] = [
  {
    path: getPath('portalPage'),
    element: lazyImport(() => import('modules/Miscellaneous/PortalPage')),
    handle: {
      crumb: () => 'Trang chủ',
    },
  },
  {
    path: getPath('notificationPage'),
    element: lazyImport(() => import('modules/Miscellaneous/NotificationPage')),
    handle: {
      crumb: () => 'Thông báo',
    },
  },

  {
    path: getPath('UserProfilePage'),
    element: lazyImport(() => import('modules/UserProfile/UserProfilePage')),
    handle: {
      crumb: () => 'Thông tin của bạn',
    },
  },

  //Group Hành Chính:
  {
    path: getPath('OfficeCalendarManagementPage'),
    handle: {
      crumb: () => 'Lịch cơ quan',
    },
    children: [
      {
        index: true,
        element: lazyImport(
          () =>
            import(
              'modules/Administrative/OfficeCalendarManagement/OfficeCalendarManagementPage'
            )
        ),
      },
      {
        path: ':id',
        element: lazyImport(
          () =>
            import(
              'modules/Administrative/OfficeCalendarManagement/AddEditOfficeCalendarEventPage'
            )
        ),
        handle: {
          crumb: match =>
            match.params.id === CreateNewParamId
              ? 'Tạo mới sự kiện'
              : 'Chỉnh sửa sự kiện',
        },
      },
    ],
  },
  {
    handle: {
      crumb: () => 'Hội đồng quản trị',
      clickable: false,
    },
    path: '/administrative/bod',
    children: [
      {
        path: 'opinions',
        handle: {
          crumb: () => 'Ý kiến biểu quyết HĐQT',
        },
        element: lazyImport(
          () => import('modules/Administrative/BodOpinionsPage')
        ),
      },
      {
        path: 'resolutions',
        handle: {
          crumb: () => 'Danh sách biên bản nghị quyết HĐQT',
        },
        element: lazyImport(
          () => import('modules/Administrative/BodResolutionsPage')
        ),
      },
      {
        path: 'reports',
        handle: {
          crumb: () => 'Danh sách báo cáo HĐQT',
        },
        element: lazyImport(
          () => import('modules/Administrative/BodReportsPage')
        ),
      },
    ],
  },

  {
    path: getPath('PayrollsPage'),
    handle: {
      crumb: () => 'Bảng lương',
    },
    children: [
      {
        index: true,
        element: lazyImport(
          () => import('modules/Administrative/PayrollManagement')
        ),
      },
      {
        path: 'create',
        element: lazyImport(
          () =>
            import('modules/Administrative/PayrollManagement/AddPayrollPage')
        ),
        handle: {
          crumb: () => 'Tạo bảng lương mới',
        },
      },
    ],
  },

  // Group Văn bản:
  {
    handle: {
      crumb: () => 'Quản lý VB nội bộ',
      clickable: false,
    },
    path: '/documents/vosa',
    children: [
      {
        path: ':documentType',
        handle: {
          crumb: () => 'Văn bản VOSA',
        },
        children: [
          {
            index: true,
            element: lazyImport(
              () => import('modules/Documents/vosa/VOSADocumentsPage')
            ),
          },
          {
            path: 'create',
            element: lazyImport(
              () => import('modules/Documents/vosa/AddEditVOSADocumentPage')
            ),
            handle: {
              crumb: () => 'Tạo mới văn bản',
            },
          },
          {
            path: ':id',
            element: lazyImport(
              () => import('modules/Documents/vosa/VOSADocumentDetailPage')
            ),
            handle: {
              crumb: () => 'Chi tiết văn bản',
            },
          },
          {
            path: ':id/edit',
            element: lazyImport(
              () => import('modules/Documents/vosa/AddEditVOSADocumentPage')
            ),
            handle: {
              crumb: () => 'Chỉnh sửa văn bản',
            },
          },
        ],
      },
    ],
  },
  {
    handle: {
      crumb: () => 'Quản lý VB nội bộ',
      clickable: false,
    },
    path: '/documents/management',
    children: [
      {
        path: ':documentManagementType',
        handle: {
          crumb: () => 'Quản trị văn bản',
        },
        children: [
          {
            index: true,
            element: lazyImport(
              () =>
                import('modules/Documents/management/DocumentManagementPage')
            ),
          },
        ],
      },
    ],
  },

  //Group Nhiệm vụ:
  {
    handle: {
      crumb: () => 'Nhiệm vụ',
    },
    path: getPath('TaskListPage'),
    children: [
      {
        index: true,
        element: lazyImport(() => import('modules/Task/TasksPage')),
      },
      {
        path: 'oversight',
        element: lazyImport(() => import('modules/Task/OversightPage')),
        handle: {
          crumb: () => 'Lãnh đạo',
        },
      },
      {
        handle: {
          crumb: () => 'Quản lý nhiệm vụ',
          clickable: false,
        },
        path: '/tasks/management',
        children: [
          {
            path: ':taskManagementType',
            handle: {
              crumb: () => 'Nhóm nhiệm vụ',
            },
            children: [
              {
                index: true,
                element: lazyImport(
                  () => import('modules/Task/ManagementPage')
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  //Group Công việc:
  {
    handle: {
      crumb: () => 'Công việc',
      clickable: false,
    },
    path: '/work',
    children: [
      {
        path: 'my-work',
        handle: {
          crumb: () => 'Công việc của tôi',
        },
        children: [
          {
            index: true,
            element: lazyImport(() => import('modules/Work/MyWorkPage')),
          },
          {
            path: 'create',
            element: lazyImport(() => import('modules/Work/AddNewWorkPage')),
            handle: {
              crumb: () => 'Tạo công việc mới',
            },
          },
          {
            path: ':id',
            element: lazyImport(() => import('modules/Work/MyWorkDetailPage')),
            handle: {
              crumb: () => 'Chi tiết công việc',
            },
          },
        ],
      },
    ],
  },

  //Group Quản trị:
  {
    handle: {
      crumb: () => 'Quản trị',
      clickable: false,
    },
    path: '/administration',
    children: [
      {
        path: 'department-management',
        handle: {
          crumb: () => 'Phòng ban',
        },
        element: lazyImport(
          () => import('modules/Administration/DepartmentManagementPage')
        ),
      },
      {
        path: 'position-management',
        handle: {
          crumb: () => 'Chức danh',
        },
        element: lazyImport(
          () => import('modules/Administration/PositionManagementPage')
        ),
      },
      {
        path: 'account-management',
        handle: {
          crumb: () => 'Tài khoản',
        },
        element: lazyImport(
          () => import('modules/Administration/AccountManagementPage')
        ),
      },
      {
        path: 'permission-management',
        handle: {
          crumb: () => 'Phân quyền',
        },
        element: lazyImport(
          () => import('modules/Administration/PermissionManagementPage')
        ),
      },
      {
        path: 'user-group-management',
        handle: {
          crumb: () => 'Nhóm người dùng',
        },
        element: lazyImport(
          () => import('modules/Administration/UserGroupManagementPage')
        ),
      },
      {
        path: 'signature-account-management',
        handle: {
          crumb: () => 'Tài khoản ký số',
        },
        element: lazyImport(
          () => import('modules/Administration/SignatureAccountManagementPage')
        ),
      },
    ],
  },
];

export const routes: TRoute[] = [...publicRoutes, ...privateRoutes];
